import { Intent as BlueprintIntent } from '@blueprintjs/core';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Loading,
  LoadingIcon,
  Modal,
  Tooltip,
} from 'vet-bones/design-system/components';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  INVITE_ORGANIZATION_DIALOG_BACK,
  INVITE_ORGANIZATION_DIALOG_CANCEL,
  INVITE_ORGANIZATION_DIALOG_NEXT,
  INVITE_ORGANIZATION_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { OrganizationStepper } from 'src/ui/containers/organization/components/OrganizationStepper';
import { InviteOrganizationSelectConnectionsStep } from 'src/ui/containers/organization/dialogs/InviteOrganizationSelectConnectionsStep';
import { InviteOrganizationSelectMembersStep } from 'src/ui/containers/organization/dialogs/InviteOrganizationSelectMembersStep';
import { useInviteOrgMembers } from 'src/ui/containers/organization/hooks/invitations';
import { useServerRoles } from 'src/ui/containers/organization/hooks/serverRoles';
import { queueMessage } from 'src/ui/features/notifications';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useListOrganizationConnectionsQuery } from 'src/ui/graph/types';
import {
  OrganizationRole,
  useInvalidateOrganizationMembers,
  useOrganization,
} from 'src/ui/hooks/organization';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const STEPS = [
  copy.components.organization.inviteDialog.steps.selectMembers,
  copy.components.organization.inviteDialog.steps.selectEndpoints,
];

export const InviteOrganizationMembersDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org = '' } = useParams<{ org?: string }>();

  const { isLoadingMembers, membersByEmail } = useOrganization(org);
  const invalidateMembers = useInvalidateOrganizationMembers(org);

  const memberEmails = React.useMemo(() => Object.keys(membersByEmail), [
    membersByEmail,
  ]);

  const isOpen = useAppSelector(
    isDialogOpenSelector(DialogType.INVITE_ORGANIZATION_MEMBERS)
  );

  const {
    mutateAsync: inviteMembers,
    isLoading: isInviting,
  } = useInviteOrgMembers();

  const {
    data: connectionsData,
    isLoading: isConnectionsLoading,
  } = useListOrganizationConnectionsQuery(
    graphQLClient,
    { org_domain: org },
    { enabled: Boolean(org) }
  );

  const defaultRolesByConnectionId = React.useMemo(
    () =>
      (connectionsData?.listDefaultOrganizationConnectionsAccess || []).reduce(
        (acc, access) => {
          if (access) {
            acc[access.connection?.id || ''] =
              access.stardog_roles?.filter((role): role is NonNullable<
                typeof role
              > => Boolean(role)) || [];
          }
          return acc;
        },
        {} as Record<string, string[]>
      ),
    [connectionsData]
  );

  const connectionItems = React.useMemo(
    () =>
      connectionsData?.listConnections?.filter((conn): conn is NonNullable<
        typeof conn
      > =>
        Boolean(conn && !conn.usesOrgSso && !conn.access?.user_is_private)
      ) || [],
    [connectionsData]
  );

  const rolesByConnectionId = useServerRoles(connectionItems, isOpen);

  const [currentStepIdx, setCurrentStepIdx] = React.useState(0);
  const [emails, setEmails] = React.useState<string[]>([]);
  const [emailsQuery, setEmailsQuery] = React.useState<string>('');
  const [emailsError, setEmailsError] = React.useState<string | null>(null);
  const [role, setRole] = React.useState<
    OrganizationRole.ADMIN | OrganizationRole.MEMBER
  >(OrganizationRole.MEMBER);
  const [connectionIds, setConnectionIds] = React.useState<string[]>([]);
  const [connectionRolesById, setConnectionRolesById] = React.useState<
    Record<string, string[]>
  >({});
  const [connectionSearch, setConnectionSearch] = React.useState<string>('');
  const [
    isDefaultConnections,
    setIsDefaultConnections,
  ] = React.useState<boolean>(true);

  const handleClose = () => {
    setCurrentStepIdx(0);
    setEmails([]);
    setEmailsError(null);
    setEmailsQuery('');
    setRole(OrganizationRole.MEMBER);
    setConnectionIds([]);
    setConnectionSearch('');
    setIsDefaultConnections(true);
    dispatch(closedDialog());
  };

  const handleNext = () => {
    setCurrentStepIdx(1);
  };

  const handleBack = () => {
    setCurrentStepIdx(0);
  };

  const handleSubmit = async () => {
    const success = await inviteMembers({
      input: {
        emails,
        organization_domain: org,
        organization_role: role,
        connection_access: isDefaultConnections
          ? undefined
          : Object.entries(connectionRolesById).map(
              ([connectionId, roles]) => ({
                connection_id: connectionId,
                stardog_roles: roles.length ? roles : undefined,
              })
            ),
      },
    });

    invalidateMembers();

    if (success) {
      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message:
            copy.components.organization.inviteDialog.notifications.success,
        })
      );
      handleClose();
    }
  };

  let stepper: JSX.Element | null = null;
  let body: JSX.Element | null = null;
  let footerActions: JSX.Element | null = null;

  if (isLoadingMembers || isConnectionsLoading) {
    body = (
      <div className="organization-dialog-loading">
        <Loading size="lg" />
      </div>
    );
    footerActions = (
      <>
        <Button
          onClick={handleClose}
          size="sm"
          testId={INVITE_ORGANIZATION_DIALOG_CANCEL}
          variant="ghost-gray"
        >
          {copy.components.organization.inviteDialog.buttons.cancel}
        </Button>
      </>
    );
  } else if (currentStepIdx === 0 && !connectionItems.length) {
    body = (
      <InviteOrganizationSelectMembersStep
        emails={emails}
        emailsError={emailsError}
        emailsQuery={emailsQuery}
        memberEmails={memberEmails}
        onSetEmails={setEmails}
        onSetEmailsError={setEmailsError}
        onSetEmailsQuery={setEmailsQuery}
        onSetRole={setRole}
        role={role}
      />
    );
    footerActions = (
      <>
        <Button
          onClick={handleClose}
          size="sm"
          testId={INVITE_ORGANIZATION_DIALOG_CANCEL}
          variant="ghost-gray"
        >
          {copy.components.organization.inviteDialog.buttons.cancel}
        </Button>
        <Tooltip
          description={
            copy.components.organization.inviteDialog.selectMembers.email
              .validationRequired
          }
          disabled={Boolean(emails.length)}
        >
          <Button
            disabled={!emails.length || isInviting}
            onClick={handleSubmit}
            rightIcon={isInviting ? LoadingIcon : undefined}
            size="sm"
            testId={INVITE_ORGANIZATION_DIALOG_SUBMIT}
            variant="primary-accent"
          >
            {copy.components.organization.inviteDialog.buttons.submit}
          </Button>
        </Tooltip>
      </>
    );
  } else if (currentStepIdx === 0) {
    stepper = (
      <OrganizationStepper currentStepIdx={currentStepIdx} steps={STEPS} />
    );
    body = (
      <InviteOrganizationSelectMembersStep
        emails={emails}
        emailsError={emailsError}
        emailsQuery={emailsQuery}
        memberEmails={memberEmails}
        onSetEmails={setEmails}
        onSetEmailsError={setEmailsError}
        onSetEmailsQuery={setEmailsQuery}
        onSetRole={setRole}
        role={role}
      />
    );
    footerActions = (
      <>
        <Button
          onClick={handleClose}
          size="sm"
          testId={INVITE_ORGANIZATION_DIALOG_CANCEL}
          variant="ghost-gray"
        >
          {copy.components.organization.inviteDialog.buttons.cancel}
        </Button>
        <Tooltip
          description={
            copy.components.organization.inviteDialog.selectMembers.email
              .validationRequired
          }
          disabled={Boolean(emails.length)}
        >
          <Button
            disabled={!emails.length}
            onClick={handleNext}
            size="sm"
            testId={INVITE_ORGANIZATION_DIALOG_NEXT}
            variant="primary-accent"
          >
            {copy.components.organization.inviteDialog.buttons.next}
          </Button>
        </Tooltip>
      </>
    );
  } else if (currentStepIdx === 1) {
    stepper = (
      <OrganizationStepper currentStepIdx={currentStepIdx} steps={STEPS} />
    );
    body = (
      <InviteOrganizationSelectConnectionsStep
        connections={connectionItems}
        connectionSearch={connectionSearch}
        defaultRolesByConnectionId={defaultRolesByConnectionId}
        isDefaultConnections={isDefaultConnections}
        onSetConnectionSearch={setConnectionSearch}
        onSetIsDefaultConnections={setIsDefaultConnections}
        onSetSelectedConnectionIds={setConnectionIds}
        onSetSelectedConnectionRolesById={setConnectionRolesById}
        rolesByConnectionId={rolesByConnectionId}
        selectedConnectionIds={connectionIds}
        selectedConnectionRolesById={connectionRolesById}
      />
    );
    footerActions = (
      <>
        <Button
          disabled={isInviting}
          onClick={handleBack}
          size="sm"
          testId={INVITE_ORGANIZATION_DIALOG_BACK}
          variant="ghost-gray"
        >
          {copy.components.organization.inviteDialog.buttons.back}
        </Button>
        <Button
          disabled={isInviting}
          onClick={handleSubmit}
          rightIcon={isInviting ? LoadingIcon : undefined}
          size="sm"
          testId={INVITE_ORGANIZATION_DIALOG_SUBMIT}
          variant="primary-accent"
        >
          {copy.components.organization.inviteDialog.buttons.submit}
        </Button>
      </>
    );
  }

  return (
    <Modal
      canOutsideClickClose={false}
      className="organization-dialog invite-organization-members-dialog"
      footerActions={footerActions}
      headerSize="lg"
      isOpen={isOpen}
      onClose={handleClose}
      title={copy.components.organization.inviteDialog.title}
    >
      <div className="organization-dialog-body">
        {stepper}
        {body}
      </div>
    </Modal>
  );
};
